import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ToastService } from 'app/domains/shared/components/toast/services/toast.service';
import { AutomationsApi } from 'app/domains/aya/apis/automations.api';
import { EnumAutomationSchedulePreset } from 'app/domains/aya/enums/automation-schedule-preset.enum';
import { IAutomation } from 'app/domains/aya/interfaces/automation.interface';
import {
  buildScheduleRequest,
  humanizeSchedule,
} from 'app/domains/aya/utils/automation-schedule.util';

const TITLE_MAX_LENGTH = 120;
const DESCRIPTION_MAX_LENGTH = 8000;
const DEFAULT_TIME = '09:00';
const DEFAULT_DAY_OF_WEEK = 1;
const MIN_DAY_OF_MONTH = 1;
const MAX_DAY_OF_MONTH = 28;

interface IPresetOption {
  preset: EnumAutomationSchedulePreset;
  label: string;
}

@Component({
  selector: 'sgt-automation-form',
  templateUrl: './automation-form.component.html',
  styleUrls: ['./automation-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AutomationFormComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() automation: IAutomation | null = null;
  @Output() saved = new EventEmitter<IAutomation>();
  @Output() cancelled = new EventEmitter<void>();

  @ViewChild('titleInput') titleInput?: ElementRef<HTMLInputElement>;

  form!: FormGroup;
  selectedPreset: EnumAutomationSchedulePreset = EnumAutomationSchedulePreset.Weekly;
  daysOfWeek: number[] = [DEFAULT_DAY_OF_WEEK];
  isSaving = false;
  submitted = false;

  readonly titleMaxLength = TITLE_MAX_LENGTH;
  readonly descriptionMaxLength = DESCRIPTION_MAX_LENGTH;
  readonly weekdayButtons = [
    { day: 0, label: 'D' },
    { day: 1, label: 'S' },
    { day: 2, label: 'T' },
    { day: 3, label: 'Q' },
    { day: 4, label: 'Q' },
    { day: 5, label: 'S' },
    { day: 6, label: 'S' },
  ];
  readonly presetOptions: IPresetOption[] = [
    { preset: EnumAutomationSchedulePreset.Daily, label: 'Todos os dias' },
    { preset: EnumAutomationSchedulePreset.Weekly, label: 'Semanalmente' },
    { preset: EnumAutomationSchedulePreset.Monthly, label: 'Mensalmente' },
  ];

  private destroy$ = new Subject<void>();
  private focusTimeoutId?: ReturnType<typeof setTimeout>;

  constructor(
    private fb: FormBuilder,
    private automationsApi: AutomationsApi,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef,
  ) {}

  get isEditing(): boolean {
    return this.automation !== null;
  }

  get showWeekdays(): boolean {
    return (
      this.selectedPreset === EnumAutomationSchedulePreset.Daily ||
      this.selectedPreset === EnumAutomationSchedulePreset.Weekly
    );
  }

  get showMonthDay(): boolean {
    return this.selectedPreset === EnumAutomationSchedulePreset.Monthly;
  }

  get summary(): string {
    return humanizeSchedule({
      preset: this.selectedPreset,
      daysOfWeek: this.daysOfWeek,
      dayOfMonth: Number(this.form.get('dayOfMonth')?.value),
      time: this.form.get('time')?.value as string,
    });
  }

  ngOnInit(): void {
    const source = this.automation;
    this.selectedPreset =
      source?.schedule.preset ?? EnumAutomationSchedulePreset.Weekly;

    this.form = this.fb.group({
      title: [
        source?.title ?? '',
        [Validators.required, Validators.maxLength(TITLE_MAX_LENGTH)],
      ],
      description: [
        source?.description ?? '',
        [Validators.required, Validators.maxLength(DESCRIPTION_MAX_LENGTH)],
      ],
      dayOfMonth: [
        source?.schedule.dayOfMonth ?? MIN_DAY_OF_MONTH,
        [Validators.min(MIN_DAY_OF_MONTH), Validators.max(MAX_DAY_OF_MONTH)],
      ],
      time: [source?.schedule.time ?? DEFAULT_TIME, Validators.required],
    });

    this.updateDayOfMonthValidation();
    this.daysOfWeek = this.initialDays(source);
  }

  ngAfterViewInit(): void {
    this.focusTimeoutId = setTimeout(() =>
      this.titleInput?.nativeElement.focus(),
    );
  }

  ngOnDestroy(): void {
    if (this.focusTimeoutId) {
      clearTimeout(this.focusTimeoutId);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  get dayOfMonthInvalid(): boolean {
    const control = this.form.get('dayOfMonth');
    return !!control && control.enabled && control.invalid;
  }

  selectPreset(preset: EnumAutomationSchedulePreset): void {
    this.selectedPreset = preset;
    if (
      preset === EnumAutomationSchedulePreset.Weekly &&
      this.daysOfWeek.length !== 1
    ) {
      this.daysOfWeek = [DEFAULT_DAY_OF_WEEK];
    }
    if (
      preset === EnumAutomationSchedulePreset.Daily &&
      !this.daysOfWeek.length
    ) {
      this.daysOfWeek = [1, 2, 3, 4, 5];
    }
    this.updateDayOfMonthValidation();
  }

  private updateDayOfMonthValidation(): void {
    const control = this.form.get('dayOfMonth');
    if (this.selectedPreset === EnumAutomationSchedulePreset.Monthly) {
      control?.enable({ emitEvent: false });
    } else {
      control?.disable({ emitEvent: false });
    }
  }

  toggleDay(day: number): void {
    if (this.selectedPreset === EnumAutomationSchedulePreset.Weekly) {
      this.daysOfWeek = [day];
      return;
    }
    this.daysOfWeek = this.daysOfWeek.includes(day)
      ? this.daysOfWeek.filter(current => current !== day)
      : [...this.daysOfWeek, day];
  }

  isDaySelected(day: number): boolean {
    return this.daysOfWeek.includes(day);
  }

  cancel(): void {
    this.cancelled.emit();
  }

  submit(): void {
    this.submitted = true;
    if (this.form.invalid || this.isSaving) {
      return;
    }

    this.isSaving = true;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const schedule = buildScheduleRequest(
      {
        preset: this.selectedPreset,
        daysOfWeek: this.daysOfWeek,
        dayOfMonth: Number(this.form.get('dayOfMonth')?.value),
        time: (this.form.get('time')?.value as string).trim(),
      },
      timezone,
    );

    const title = (this.form.get('title')?.value as string).trim();
    const description = (this.form.get('description')?.value as string).trim();

    const request$ = this.automation
      ? this.automationsApi.update(this.automation.id, {
          version: this.automation.version,
          title,
          description,
          schedule,
        })
      : this.automationsApi.create({ title, description, schedule, active: true });

    request$.pipe(takeUntil(this.destroy$)).subscribe({
      next: automation => {
        this.isSaving = false;
        this.toastService.success(
          this.isEditing ? 'Automação atualizada.' : 'Automação criada.',
        );
        this.saved.emit(automation);
      },
      error: error => {
        this.isSaving = false;
        this.toastService.error(
          this.backendMessage(error) ?? 'Não foi possível salvar a automação.',
        );
        this.cdr.markForCheck();
      },
    });
  }

  private backendMessage(error: unknown): string | null {
    const body = (error as { error?: { message?: string; error?: { message?: string } } })?.error;
    return body?.message ?? body?.error?.message ?? null;
  }

  private initialDays(source: IAutomation | null): number[] {
    if (!source) {
      return [DEFAULT_DAY_OF_WEEK];
    }
    if (source.schedule.preset === EnumAutomationSchedulePreset.Weekly) {
      return [source.schedule.dayOfWeek ?? DEFAULT_DAY_OF_WEEK];
    }
    return [...source.schedule.daysOfWeek];
  }
}
